import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Button, Col, Row, UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";

import BadgeList from "components/Common/BadgeList";
import { CanView } from "components/Common/CanView";
import DeleteModal from "components/Common/DeleteModal";
import GlobalInputSearch from "components/Common/GlobalInputSearch";
import PageWrapper from "components/Common/PageWrapper";
import TableContainer from "components/Common/TableContainer";
import { getProfileImageUrl } from "components/Common/ProfileImageUploader";
import { ACCESS } from "constants/accessPermissions";
import { useAuth } from "hooks/authHook";
import { useDeleteEmployee, useEmployees, useImpersonateEmployee } from "hooks/employeeHook";
import { useImageUrl } from "hooks/imageHook";
import { useListParams } from "hooks/useListParams";
import { formatReadableDate } from "utils/formatDate";
import { getToken, setOriginalAuthUser, setOriginalToken, setToken } from "utils/helpersUtils";
import EmployeeAddEdit from "./EmployeeAddEdit";

const SYSTEM_ACCESS_BLOCKED_STATUSES = ["inactive", "resigned", "terminated"]

const humanizeStatus = status =>
  status
    ? status
        .split("_")
        .join(" ")
        .replace(/\b\w/g, char => char.toUpperCase())
    : "Unknown"

const AVATAR_SIZE = 40

// Resolve the employee record to an image-id we can fetch, or a pre-baked URL.
const resolveEmployeeImage = employee => {
  if (!employee) return { id: null, url: null }

  // 1) Nested image object (some endpoints inline it).
  const nested = employee.image
  if (nested) {
    if (typeof nested === "string") return { id: null, url: nested }
    if (nested.url) return { id: null, url: nested.url }
    if (nested.id) return { id: nested.id, url: null }
  }

  // 2) Foreign key to /images/{id}.
  const id = employee.image_id ?? employee.imageId ?? null
  if (id) return { id, url: null }

  // 3) Fall back to the linked user's profile_image (covers shared user avatars).
  const userUrl = getProfileImageUrl(employee.user)
  if (userUrl) return { id: null, url: userUrl }

  return { id: null, url: null }
}

const EmployeeAvatar = ({ employee }) => {
  const { id: imageId, url: inlineUrl } = resolveEmployeeImage(employee)
  const { url: fetchedUrl } = useImageUrl(imageId)

  const imageUrl = inlineUrl || fetchedUrl
  const initial = (employee?.name || employee?.user?.username || "?")
    .charAt(0)
    .toUpperCase()

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={employee?.name || "Employee"}
        className="rounded-circle flex-shrink-0"
        style={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          objectFit: "cover",
        }}
      />
    )
  }

  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center text-primary fw-bold flex-shrink-0"
      style={{
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        background: "#eef2ff",
        fontSize: 16,
      }}
    >
      {initial}
    </div>
  )
}

const EmployeeList = () => {
  document.title = "Employee List"

  const navigate = useNavigate()

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selected, setSelected] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [impersonatingId, setImpersonatingId] = useState(null)

  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      per_page: 10,
      ...(params.search ? { search: params.search } : {}),
    }),
    [params]
  )

  const {
    data: response,
    isLoading,
    isRefetching,
    refetch,
  } = useEmployees(queryParams)

  const { mutateAsync: removeEmployee, isPending: deleteLoading } = useDeleteEmployee()

  const { user: authUser, isImpersonating: hasActiveImpersonation } = useAuth()
  const { mutateAsync: impersonateEmployee, isPending: isImpersonating } = useImpersonateEmployee()

  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setSelected(null)
      setIsEdit(false)
    } else {
      setModal(true)
    }
  }, [modal])

  const handleAddNew = useCallback(() => {
    setSelected(null)
    setIsEdit(false)
    setModal(true)
  }, [])

  const handleEdit = useCallback(item => {
    setSelected(item)
    setIsEdit(true)
    setModal(true)
  }, [])

  const handleView = useCallback(
    item => {
      navigate(`/employee-list/${item.id}/view`)
    },
    [navigate]
  )

  const handleDeleteClick = useCallback(item => {
    setSelected(item)
    setDeleteModal(true)
  }, [])

  const handleImpersonate = useCallback(
    async item => {
      setImpersonatingId(item.id)

      try {
        const res = await impersonateEmployee(item.id)
        if (res?.data?.access_token) {
          setOriginalToken(getToken())
          if (authUser) {
            setOriginalAuthUser(authUser)
          }
          setToken(res.data.access_token)
          toast.success(res.message || "Successfully impersonated user")
          window.location.replace("/dashboard")
        }
      } catch (error) {
        console.error(error)
        toast.error(error?.response?.data?.message ?? "Failed to impersonate user.")
      } finally {
        setImpersonatingId(null)
      }
    },
    [impersonateEmployee, authUser, navigate]
  )

  const handleDelete = useCallback(async () => {
    if (!selected?.id) return
    try {
      const res = await removeEmployee(selected.id)
      toast.success(res?.message ?? "Employee deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (error) {
      console.error(error)
      toast.error(error?.response?.data?.message ?? "Failed to delete employee.")
    }
  }, [selected, removeEmployee, refetch])

  const columns = useMemo(
    () => [
      {
        header: "Employee",
        cell: ({ row }) => {
          const e = row.original
          return (
            <div className="d-flex align-items-center gap-2 min-width-0">
              <EmployeeAvatar employee={e} />
              <div className="min-width-0">
                <div className="fw-semibold text-truncate">{e.name}</div>
                <small className="text-muted">
                  {e.employee_id ? `#${e.employee_id}` : "—"}
                </small>
              </div>
            </div>
          )
        },
      },
      {
        header: "Contact",
        cell: ({ row }) => {
          const e = row.original
          return (
            <div>
              <div>
                <i className="bx bx-phone" /> {e.mobile || "—"}
              </div>
              <div>
                <i className="bx bx-envelope" /> {e.email || "—"}
              </div>
            </div>
          )
        },
      },
      {
        header: "Designation / Department",
        cell: ({ row }) => {
          const e = row.original
          return (
            <div>
              <div>{e.designation?.title || "—"}</div>
              <small className="text-muted">{e.department?.name || "—"}</small>
            </div>
          )
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const status = row.original.status || "active"
          const colorMap = {
            active: "success",
            inactive: "secondary",
            on_leave: "warning",
            resigned: "danger",
            terminated: "danger",
          }
          return (
            <Badge color={colorMap[status] || "secondary"} pill>
              {status.replace("_", " ")}
            </Badge>
          )
        },
      },
      {
        header: "System Access",
        cell: ({ row }) => {
          const e = row.original
          const isRevoked = !!e.user?.deleted_at
          const isStatusBlocked = SYSTEM_ACCESS_BLOCKED_STATUSES.includes(e.status)

          if (!e.user_id) {
            return (
              <span className={`small ${isStatusBlocked ? "text-danger" : "text-muted"}`}>
                {isStatusBlocked ? `Blocked by ${humanizeStatus(e.status)}` : "No login"}
              </span>
            )
          }

          const roles = e.user?.roles ?? []

          return (
            <div style={{ maxWidth: 220 }}>
              <div className={`${isRevoked || isStatusBlocked ? "text-danger" : "text-success"} small`}>
                <i className={`bx ${isRevoked || isStatusBlocked ? "bx-block" : "bx-check-circle"}`} />{" "}
                {e.user?.username}
              </div>
              {isRevoked || isStatusBlocked ? (
                <div className="text-muted small mb-1">
                  {isStatusBlocked ? `Blocked by ${humanizeStatus(e.status)}` : "Access revoked"}
                </div>
              ) : null}
              <BadgeList items={roles} />
            </div>
          )
        },
      },
      {
        header: "Joined",
        accessorKey: "joining_date",
        cell: ({ row }) =>
          row.original.joining_date
            ? formatReadableDate(row.original.joining_date)
            : "—",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const e = row.original
          const isRevoked = !!e.user?.deleted_at
          const isStatusBlocked = SYSTEM_ACCESS_BLOCKED_STATUSES.includes(e.status)

          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <CanView access={[ACCESS.EMPLOYEE.VIEW]}>
                <li>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => handleView(e)}
                    id={`view-emp-${e.id}`}
                  >
                    <i className="mdi mdi-eye-outline" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`view-emp-${e.id}`}>
                    View
                  </UncontrolledTooltip>
                </li>
              </CanView>

              <CanView access={[ACCESS.EMPLOYEE.GRANT_ACCESS]}>
                {e.user_id && !isRevoked && !isStatusBlocked && (
                  <li>
                    <Button
                      color="warning"
                      size="sm"
                      onClick={() => handleImpersonate(e)}
                      id={`impersonate-emp-${e.id}`}
                      disabled={impersonatingId === e.id || hasActiveImpersonation}
                    >
                      {impersonatingId === e.id ? (
                        <i className="bx bx-loader bx-spin font-size-16 align-middle" />
                      ) : (
                        <i className="mdi mdi-account-convert" />
                      )}
                    </Button>
                    <UncontrolledTooltip placement="top" target={`impersonate-emp-${e.id}`}>
                      {hasActiveImpersonation
                        ? "Return to your main account before starting another impersonation"
                        : "Impersonate Login"}
                    </UncontrolledTooltip>
                  </li>
                )}
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE.EDIT]}>
                <li>
                  <Button
                    color="info"
                    size="sm"
                    onClick={() => handleEdit(e)}
                    id={`edit-emp-${e.id}`}
                  >
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`edit-emp-${e.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </li>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE.DELETE]}>
                <li>
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => handleDeleteClick(e)}
                    id={`delete-emp-${e.id}`}
                  >
                    <i className="mdi mdi-delete-outline" />
                  </Button>
                  <UncontrolledTooltip placement="top" target={`delete-emp-${e.id}`}>
                    Delete
                  </UncontrolledTooltip>
                </li>
              </CanView>
            </ul>
          )
        },
      },
    ],
    [handleEdit, handleDeleteClick, handleView, handleImpersonate, impersonatingId, hasActiveImpersonation]
  )

  const employees = response?.data?.data ?? []
  const paginationInfo = response?.data ?? null

  return (
    <PageWrapper title="Employees" breadcrumbItem="Employee List">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="mb-3 align-items-center">
        <Col sm={6}>
          <h5 className="mb-0">Employee List</h5>
        </Col>
        <Col sm={6} className="text-end">
          <CanView access={[ACCESS.EMPLOYEE.ADD]}>
            <Button color="primary" onClick={handleAddNew} className="me-1">
              <i className="bx bx-plus" /> Add Employee
            </Button>
          </CanView>
          <Button color="light" onClick={() => refetch()} id="refresh-emp">
            <i
              className={`mdi mdi-refresh ${isRefetching ? "mdi-spin" : ""}`}
            />
          </Button>
          <UncontrolledTooltip placement="top" target="refresh-emp">
            Refresh
          </UncontrolledTooltip>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={employees}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline mt-2 border-top"
      />

      <EmployeeAddEdit
        modal={modal}
        toggleModal={toggleModal}
        isEdit={isEdit}
        editedData={selected}
      />
    </PageWrapper>
  )
}

export default EmployeeList
